// Story 7.10 (ADO-906), AC1/AC3/AC5: the attribute FilterPanel, pixel-ported to
// the designer prototype's filters rail (Epic 7 re-skin, ADO-903).
//
// Story 7.29 (ADO-2025): the rail renders exactly the catalogue narrowings the listing (or
// search) returned (`narrowings`): the members its range and account are offered. A member the
// range does not offer is hidden. A member it offers but that nothing in scope carries
// (`unavailableNarrowings`) is shown disabled, as ADO-3692 D-10 decided (D-7.29-25); a selected
// one stays enabled so it can be cleared. The server applies no other narrowing but the leaflet
// destination (D-7.29-20) and Story 7.32's Featured Lines (ADO-1115), which gate themselves: their
// toggle is rendered only while the facet offers the value, so every enabled option changes the
// request.
//
// The checkboxes go to the wire through ADO-3692's `FACET_TOGGLES` (its D-9). New Lines and New
// Listings both drive `newListing` (its D-6). The prototype's options with no SL2 data (My
// Regulars, Top Sellers, RBP Core Lines, Value Projection, All Promotions, Handbill, Price,
// Margin, Price Marked, Alcohol RRP) are not rendered until their data exists (D-7.29-6, a
// deviation from the Figma rail). Country of Origin lists the data's values with their counts
// (ADO-3692 D-5), and the Product heading counts its selections (ADO-3692 D-11). An entry switch
// re-reads the listing, so the rail follows the new range.
//
// Prototype anatomy (spec-epic7-category-listing-pixel-parity, task 3): Show Me ->
// Deals & Listing Type -> Department (collapsed) -> Category (collapsed) -> Product Information
// (Country of Origin, then Brand, then Product Size; Figma 6603:98073, agreed at the Epic 7 3 amigos
// on 2026-11-02). Each is a searchable SINGLE-select over its real facet with a clear X once chosen
// (the Brand field's shape, given to the other two on 2026-11-03). The single-selects flow through
// the existing ProductFilters shape as one-element arrays (`{ brand: [value] }`).
//
// a11y (NFR24-26): sections are <fieldset>s with a <legend>; a collapsible legend
// carries the expand/collapse control with `aria-expanded`; multi-value rows are
// the design-system <Checkbox>/<RadioGroup> (native inputs, correct accessible
// name and keyboard operability); the search-selects are labelled inputs above a
// plain list of real buttons, the active value marked with `aria-current` (no
// listbox/option ARIA: see the SearchSelect note for why those roles were dropped).

import { type ReactNode, useEffect, useId, useRef, useState } from 'react';
import { Checkbox, Icon, RadioGroup } from '../../design-system';
import {
  FACET_TOGGLES,
  type CatalogueNarrowing,
  type Facet,
  type FacetKey,
  type HierarchyFacetKey,
  type ProductFilters,
} from '../../api/facets';
import type { CategoryNode } from '../../api/browse.client';
import { formatCategoryName } from './categoryDisplay';
import styles from './FilterPanel.module.css';

const ANY_DEPARTMENT = '__any__';

// The searchable dropdowns cap their visible result rows (prototype slice(0, 50)).
const MAX_DROPDOWN_RESULTS = 50;

/** The RBP core option: the two not-purchased options are drawn only beside it. */
const RBP_CORE_LABEL = 'RBP Core Lines';

/** A rail checkbox: its label (a `FACET_TOGGLES` key), its narrowing, and its section. A toggle
 *  with no narrowing (`null`) gates itself: it is rendered only while the facet offers its value,
 *  or while it is ticked, and never disabled (Story 7.32's Featured Lines). */
interface RailToggle {
  readonly label: string;
  readonly narrowing: CatalogueNarrowing | null;
  readonly section: 'showMe' | 'deals';
  /** The RBP toggles: the server offers their facet value to a participant whatever its count,
   *  so a self-gated one of these is live on the value alone. */
  readonly offeredAtAnyCount?: boolean;
  /** One of the two not-purchased options, drawn indented under the core option and only
   *  beside it. */
  readonly rbpNotPurchased?: boolean;
}

/** The rail's checkboxes, in rail order. Each one's wire value is `FACET_TOGGLES[label]`. */
const RAIL_TOGGLES: readonly RailToggle[] = [
  { label: 'On Offer', narrowing: 'onOffer', section: 'showMe' },
  { label: 'New Lines', narrowing: 'newListing', section: 'showMe' },
  // The RBP options. No catalogue narrowing: the server offers their facet values only to a
  // participant in a catalogue that shows RBP, so elsewhere they are not drawn at all. SL1.0's
  // order and words (`get_smanager.php:6016-6021`); the not-purchased facet takes one value at a
  // time, which `rbpFilterCoupling` keeps true of the checkboxes.
  { label: RBP_CORE_LABEL, narrowing: null, section: 'showMe', offeredAtAnyCount: true },
  {
    label: 'Show Non-Purchased Only (RBP)',
    narrowing: null,
    section: 'showMe',
    offeredAtAnyCount: true,
    rbpNotPurchased: true,
  },
  {
    label: 'Show Not Purchased Current Quarter (RBP)',
    narrowing: null,
    section: 'showMe',
    offeredAtAnyCount: true,
    rbpNotPurchased: true,
  },
  { label: 'New Listings', narrowing: 'newListing', section: 'deals' },
  { label: 'Own Brand', narrowing: 'ownBrand', section: 'deals' },
  { label: 'Mixed Case', narrowing: 'mixedCase', section: 'deals' },
  { label: 'Small Case', narrowing: 'smallCase', section: 'deals' },
  { label: 'Leaflet Lines', narrowing: 'leafletLine', section: 'deals' },
  // Story 7.32 (ADO-1115): the lines BWG is backing this period. No catalogue narrowing: the
  // server resolves them per caller and returns the facet value only where there are some, so
  // where nothing is featured nothing about Featured Lines is put to the Shopper (AC2).
  { label: 'Featured Lines', narrowing: null, section: 'deals' },
  { label: 'Multi Buy', narrowing: 'multiBuy', section: 'deals' },
];

export interface FilterPanelProps {
  /** The server facets returned for the current listing (values and counts). */
  readonly facets: readonly Facet[];
  /** Story 7.29 (ADO-2025): the catalogue narrowings the listing offers; the rail renders these
   *  and no other. */
  readonly narrowings: readonly CatalogueNarrowing[];
  /** Story 7.29 (ADO-2025, D-7.29-25): the offered narrowings with nothing to narrow in scope,
   *  rendered disabled. */
  readonly unavailableNarrowings?: readonly CatalogueNarrowing[];
  /** The currently-selected facet values per facet (host-owned). */
  readonly selected: ProductFilters;
  /** Toggle one value within a facet (add/remove). */
  readonly onToggleValue: (key: FacetKey, value: string) => void;
  /** Clear every active attribute filter, restoring the unfiltered set (AC4). */
  readonly onClearAll: () => void;
  /** True when at least one attribute filter is active. */
  readonly hasActive: boolean;
  /** All Departments (top-level nodes) for the Department radio group. Omitted on
   *  surfaces with no taxonomy context (e.g. keyword search), so the section is hidden. */
  readonly departments?: readonly CategoryNode[];
  /** The categories under the current department for the Category section. */
  readonly categories?: readonly CategoryNode[];
  /** The department the current listing sits under (root of the breadcrumb). */
  readonly currentDepartmentId?: string | null;
  /** The category the current listing sits under, when drilled that far. */
  readonly currentCategoryId?: string | null;
  /** Navigate to a taxonomy node (`null` = Shop root): drives Department/Category. */
  readonly onNavigate?: (node: CategoryNode | null) => void;
  /**
   * Story 7.31 (ADO-1113): "On Offer" is the collected-offers union (`?offers=all`, every live
   * offer of every kind), not the promotions facet. Given, the checkbox shows whether the union
   * is on and toggles it; it stays enabled, as the server reports no "nothing on offer" gate for
   * the union. Omitted, it keeps the `FACET_TOGGLES` promotions value.
   */
  readonly collectedOffers?:
    | {
        readonly on: boolean;
        readonly onChange: (on: boolean) => void;
      }
    | undefined;
}

export function FilterPanel({
  facets,
  narrowings,
  unavailableNarrowings = [],
  selected,
  onToggleValue,
  onClearAll,
  hasActive,
  departments = [],
  categories = [],
  currentDepartmentId = null,
  currentCategoryId = null,
  onNavigate,
  collectedOffers,
}: FilterPanelProps) {
  const offers = (member: CatalogueNarrowing) => narrowings.includes(member);
  const isUnavailable = (member: CatalogueNarrowing) => unavailableNarrowings.includes(member);
  const departmentRadioValue = currentDepartmentId ?? ANY_DEPARTMENT;
  const categoryCount = currentCategoryId === null ? 0 : 1;
  const showsCategory = offers('category');

  // The value facets, surfaced as prototype-style SINGLE-selects. The wire shape stays the shared
  // ProductFilters (one-element array), so the host fetch/chips/clear plumbing is untouched. An
  // offered one with no values in scope is rendered disabled (D-7.29-25).
  const valuesOf = (key: FacetKey) => facets.find((facet) => facet.key === key)?.values ?? [];
  const countryCounts = new Map(valuesOf('countryOfOrigin').map((v) => [v.value, v.count]));
  const selectedBrand = selected.brand?.[0] ?? null;
  const selectedPack = selected.packSize?.[0] ?? null;
  const selectedCountry = selected.countryOfOrigin?.[0] ?? null;
  const productCount = [selectedCountry, selectedBrand, selectedPack].filter(
    (value) => value !== null,
  ).length;
  const disables = (member: CatalogueNarrowing, current: string | null) =>
    isUnavailable(member) && current === null;

  // While the RBP core filter is on, the Department and Category menus offer only the nodes
  // holding a product in the filtered set (SL1.0 `get_smanager.php:2810-2887`): the server's
  // hierarchy facets, by node id. The node the listing sits under stays offered so it can be left.
  const rbpOn = selected.offerType?.includes(FACET_TOGGLES[RBP_CORE_LABEL]?.value ?? '') ?? false;
  // A response with no hierarchy facet (the one still shown while the refetch after ticking the
  // toggle is in flight) narrows nothing, so the menus never flash empty.
  const nodesIn = (key: HierarchyFacetKey): ReadonlySet<string> | null => {
    const facet = facets.find((f) => f.key === key);
    return facet === undefined ? null : new Set(facet.values.map((v) => v.value));
  };
  const narrow = <T extends { id: string }>(
    nodes: readonly T[],
    key: HierarchyFacetKey,
    currentId: string | null,
  ): readonly T[] => {
    const held = rbpOn ? nodesIn(key) : null;
    return held === null ? nodes : nodes.filter((n) => held.has(n.id) || n.id === currentId);
  };
  const shownDepartments = narrow(departments, 'department', currentDepartmentId);
  const shownCategories = narrow(categories, 'category', currentCategoryId);

  const wireOf = (toggle: RailToggle) => FACET_TOGGLES[toggle.label];
  /** The toggle that drives the collected-offers union, where the host wires one. */
  const isUnionToggle = (toggle: RailToggle) =>
    collectedOffers !== undefined && toggle.narrowing === 'onOffer';
  const isChecked = (toggle: RailToggle) => {
    if (collectedOffers !== undefined && isUnionToggle(toggle)) return collectedOffers.on;
    const wire = wireOf(toggle);
    return wire !== undefined && (selected[wire.key]?.includes(wire.value) ?? false);
  };
  /** A self-gated toggle's value is in the facets with a count, or it is ticked. An RBP toggle's
   *  value is offered whatever its count. */
  const isLive = (toggle: RailToggle) => {
    const wire = wireOf(toggle);
    return (
      wire !== undefined &&
      (isChecked(toggle) ||
        valuesOf(wire.key).some(
          (value) =>
            value.value === wire.value && (toggle.offeredAtAnyCount === true || value.count > 0),
        ))
    );
  };
  const rbpCoreToggle = RAIL_TOGGLES.find((toggle) => toggle.label === RBP_CORE_LABEL);
  const rbpCoreLive = rbpCoreToggle !== undefined && isLive(rbpCoreToggle);
  const togglesIn = (section: RailToggle['section']) =>
    RAIL_TOGGLES.filter(
      (toggle) =>
        toggle.section === section &&
        (toggle.narrowing === null
          ? isLive(toggle)
          : offers(toggle.narrowing) || (isUnionToggle(toggle) && isChecked(toggle))) &&
        (toggle.rbpNotPurchased !== true || rbpCoreLive) &&
        wireOf(toggle) !== undefined,
    );
  const checkedCount = (toggles: readonly RailToggle[]) => toggles.filter(isChecked).length;
  const showMe = togglesIn('showMe');
  const deals = togglesIn('deals');

  /** Replace-semantics single-select over the existing toggle wiring: remove the
   *  previous value (if any), then add the next one. Both go through the host's
   *  functional set-state, so the two calls compose to `{ key: [value] }`. */
  const applySingle = (key: FacetKey, current: string | null) => (value: string | null) => {
    if (value === current) return;
    if (current !== null) onToggleValue(key, current);
    if (value !== null) onToggleValue(key, value);
  };

  const toggleRows = (toggles: readonly RailToggle[]) => (
    <ul className={styles.values}>
      {toggles.map((toggle) => {
        const checked = isChecked(toggle);
        return (
          <li
            key={toggle.label}
            className={
              toggle.rbpNotPurchased === true
                ? `${styles.value} ${styles.rbpOption} ${styles.rbpSubOption}`
                : toggle.label === RBP_CORE_LABEL
                  ? `${styles.value} ${styles.rbpOption}`
                  : styles.value
            }
          >
            <Checkbox
              checked={checked}
              disabled={
                !checked &&
                !isUnionToggle(toggle) &&
                toggle.narrowing !== null &&
                isUnavailable(toggle.narrowing)
              }
              onChange={() => {
                if (collectedOffers !== undefined && isUnionToggle(toggle)) {
                  collectedOffers.onChange(!checked);
                  return;
                }
                const wire = wireOf(toggle);
                if (wire !== undefined) onToggleValue(wire.key, wire.value);
              }}
              label={<span className={styles.valueName}>{toggle.label}</span>}
            />
          </li>
        );
      })}
    </ul>
  );

  return (
    <aside className={styles.panel} aria-label="Filters">
      <div className={styles.header}>
        <h2 className={styles.heading}>Filters</h2>
        {/* A department other than "Any" narrows the listing too, so it offers Clear (Epic 7
            3 amigos, 2026-11-02): Clear returns the drill to the root, which is "Any". */}
        {(hasActive || currentCategoryId !== null || currentDepartmentId !== null) && (
          <button type="button" className={styles.clear} onClick={onClearAll}>
            Clear
          </button>
        )}
      </div>

      {showMe.length > 0 && (
        <StaticSection label="Show Me" badge={checkedCount(showMe)}>
          {toggleRows(showMe)}
        </StaticSection>
      )}

      {deals.length > 0 && (
        <CollapsibleSection
          label="Deals & Listing Type"
          badge={checkedCount(deals)}
          defaultExpanded
        >
          {toggleRows(deals)}
        </CollapsibleSection>
      )}

      {/* Department: real taxonomy; selecting navigates (like a breadcrumb hop).
          Collapsed by default, per the prototype. */}
      {showsCategory && shownDepartments.length > 0 && (
        <CollapsibleSection label="Department" badge={currentDepartmentId === null ? 0 : 1}>
          <div className={styles.radioRows}>
            {/* No explicit `name`: CategoryListing mounts this panel twice (desktop
                rail + mobile FiltersDrawer), so a hardcoded name would merge both
                radio sets into one native group: arrow-key navigation would span
                both instances and their checked states would fight. RadioGroup falls
                back to a per-instance `useId` name (same fix as the section ids). */}
            <RadioGroup
              label="Department"
              value={departmentRadioValue}
              onChange={(value) => {
                if (value === ANY_DEPARTMENT) {
                  onNavigate?.(null);
                  return;
                }
                const dept = departments.find((d) => d.id === value);
                if (dept) onNavigate?.(dept);
              }}
              options={[
                { value: ANY_DEPARTMENT, label: 'Any' },
                ...shownDepartments.map((d) => ({
                  value: d.id,
                  label: formatCategoryName(d.name),
                })),
              ]}
            />
          </div>
        </CollapsibleSection>
      )}

      {/* Category: real; single-active (selecting navigates into that category).
          Collapsed by default, per the prototype. */}
      {showsCategory && shownCategories.length > 0 && (
        <CollapsibleSection label="Category" badge={categoryCount}>
          <ul className={styles.values}>
            {shownCategories.map((cat) => {
              const isActive = cat.id === currentCategoryId;
              return (
                <li key={cat.id} className={styles.value}>
                  <Checkbox
                    checked={isActive}
                    onChange={() => onNavigate?.(isActive ? null : cat)}
                    label={<span className={styles.valueName}>{formatCategoryName(cat.name)}</span>}
                  />
                </li>
              );
            })}
          </ul>
        </CollapsibleSection>
      )}

      {/* Product Information: Country of Origin (the countries in scope, with counts), Brand and
          Product Size, all over the real facets. The pill counts all three (ADO-3692 D-11). */}
      {(offers('countryOfOrigin') || offers('brand') || offers('packSize')) && (
        <div className={styles.section} data-section="product-information">
          <p className={styles.sectionTitle}>
            Product Information
            {productCount > 0 && (
              <span className={styles.pill} aria-hidden="true">
                {productCount}
              </span>
            )}
          </p>
          {offers('countryOfOrigin') && (
            // The Brand-style search box (Epic 7 3 amigos follow-ups, 2026-11-03), its rows still
            // labelled with their counts (ADO-3692 D-5).
            <div className={styles.fieldLabel}>
              <span>Country of Origin</span>
              <SearchSelect
                inputLabel="Search country of origin"
                placeholder="Search country…"
                values={valuesOf('countryOfOrigin').map((v) => v.value)}
                optionLabel={(value) => `${value} (${countryCounts.get(value) ?? 0})`}
                selected={selectedCountry}
                disabled={disables('countryOfOrigin', selectedCountry)}
                onSelect={applySingle('countryOfOrigin', selectedCountry)}
                clearLabel="Clear country of origin"
              />
            </div>
          )}
          {offers('brand') && (
            <div className={styles.fieldLabel}>
              <span>Brand</span>
              <SearchSelect
                inputLabel="Search brand"
                placeholder="Search brand…"
                values={valuesOf('brand').map((v) => v.value)}
                selected={selectedBrand}
                disabled={disables('brand', selectedBrand)}
                onSelect={applySingle('brand', selectedBrand)}
                clearLabel="Clear brand"
              />
            </div>
          )}
          {offers('packSize') && (
            <div className={styles.fieldLabel}>
              <span>Product Size</span>
              <SearchSelect
                inputLabel="Product size"
                placeholder="e.g. 1Ltr, 330ml"
                values={valuesOf('packSize').map((v) => v.value)}
                selected={selectedPack}
                disabled={disables('packSize', selectedPack)}
                onSelect={applySingle('packSize', selectedPack)}
                clearLabel="Clear product size"
              />
            </div>
          )}
        </div>
      )}
    </aside>
  );
}

/** A non-collapsible section (fieldset + legend heading + applied-count pill). */
function StaticSection({
  label,
  badge = 0,
  children,
}: {
  label: string;
  badge?: number;
  children: ReactNode;
}) {
  return (
    <fieldset className={`${styles.section} ${styles.firstSection}`}>
      <legend className={styles.staticLegend}>
        {label}
        {badge > 0 && (
          <span className={styles.pill} aria-hidden="true">
            {badge}
          </span>
        )}
      </legend>
      {children}
    </fieldset>
  );
}

/** A collapsible section: legend button with a chevron + optional count pill. */
function CollapsibleSection({
  label,
  badge = 0,
  defaultExpanded = false,
  children,
}: {
  label: string;
  badge?: number;
  defaultExpanded?: boolean;
  children: ReactNode;
}) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  // useId (not a label-derived slug): the panel renders twice on small screens
  // (rail + drawer), so slugs would collide.
  const contentId = useId();
  return (
    <fieldset className={styles.section}>
      <legend className={styles.legend}>
        <button
          type="button"
          className={styles.legendButton}
          aria-expanded={expanded}
          aria-controls={contentId}
          onClick={() => setExpanded((v) => !v)}
        >
          <span className={styles.legendLabel}>
            {label}
            {badge > 0 && (
              <span className={styles.pill} aria-hidden="true">
                {badge}
              </span>
            )}
          </span>
          <Icon name={expanded ? 'caretDown' : 'caretRight'} size={18} className={styles.chevron} />
        </button>
      </legend>
      <div id={contentId} hidden={!expanded}>
        {children}
      </div>
    </fieldset>
  );
}

interface SearchSelectProps {
  /** Accessible name of the text input (and its dropdown listbox). */
  readonly inputLabel: string;
  readonly placeholder: string;
  /** The full candidate values (real facet values from the API). */
  readonly values: readonly string[];
  /** A row's text, the value itself unless set (Country of Origin adds its count). Typing still
   *  matches the value, and the field shows the chosen value bare. */
  readonly optionLabel?: (value: string) => string;
  readonly selected: string | null;
  /** `null` clears the selection. */
  readonly onSelect: (value: string | null) => void;
  /** Story 7.29 (ADO-2025, D-7.29-25): an offered field with nothing to narrow. */
  readonly disabled?: boolean;
  /** The accessible name of an X inside the field that clears a chosen value (Figma "Clear · X"
   *  7819:51148); no X without one. */
  readonly clearLabel?: string;
}

/** Prototype searchable single-select: a small input whose dropdown lists the
 *  matching facet values (max 50), with a "Clear selection" row when one is
 *  active. Clearing the input text also clears the selection (prototype parity). */
function SearchSelect({
  inputLabel,
  placeholder,
  values,
  optionLabel = (value) => value,
  selected,
  onSelect,
  disabled = false,
  clearLabel,
}: SearchSelectProps) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  // Close on an outside pointer-down (instead of the prototype's blur+setTimeout,
  // which races the option click).
  useEffect(() => {
    if (!open) return;
    const onDocDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', onDocDown);
    return () => document.removeEventListener('mousedown', onDocDown);
  }, [open]);

  const q = query.trim().toLowerCase();
  const matches = (q === '' ? values : values.filter((v) => v.toLowerCase().includes(q))).slice(
    0,
    MAX_DROPDOWN_RESULTS,
  );

  const choose = (value: string | null) => {
    onSelect(value);
    setQuery('');
    setOpen(false);
  };

  return (
    <div className={styles.searchSelect} ref={rootRef}>
      <input
        type="text"
        className={styles.searchInput}
        aria-label={inputLabel}
        placeholder={placeholder}
        disabled={disabled}
        value={query === '' ? (selected ?? '') : query}
        onChange={(e) => {
          setQuery(e.target.value);
          if (e.target.value === '') onSelect(null);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={(e) => {
          // Only swallow Escape when THIS dropdown is open, and stop it there
          // (PR #139 review): on mobile FilterPanel is the FiltersDrawer body, and
          // the drawer closes on a document-level Escape — without this, one Escape
          // meant to dismiss the open dropdown would also collapse the whole drawer
          // (mirrors SubstitutesPanel). A closed dropdown lets Escape bubble so the
          // drawer can still be dismissed from the input.
          if (e.key === 'Escape' && open) {
            e.stopPropagation();
            setOpen(false);
            setQuery('');
          }
        }}
      />
      {clearLabel !== undefined && selected !== null && !disabled && (
        <button
          type="button"
          className={styles.searchClear}
          aria-label={clearLabel}
          onClick={() => choose(null)}
        >
          <Icon name="x" size={16} />
        </button>
      )}
      {open && (matches.length > 0 || (selected !== null && query === '')) && (
        // PR #139 review: a labelled list of real buttons, NOT an ARIA listbox —
        // the `<li>` wrappers broke listbox `aria-required-children` and there's no
        // listbox keyboard model (arrow keys / aria-activedescendant) to back the
        // roles. Same treatment as the SearchTypeahead dropdown; the active value is
        // marked with `aria-current` (was the dropped `role=option`/`aria-selected`).
        <ul className={styles.dropdown} aria-label={inputLabel}>
          {selected !== null && query === '' && (
            <li>
              <button
                type="button"
                className={`${styles.option} ${styles.optionMuted}`}
                onClick={() => choose(null)}
              >
                Clear selection
              </button>
            </li>
          )}
          {matches.map((v) => (
            <li key={v}>
              <button
                type="button"
                aria-current={selected === v ? 'true' : undefined}
                className={`${styles.option} ${selected === v ? styles.optionSelected : ''}`}
                onClick={() => choose(v)}
              >
                {optionLabel(v)}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
